import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { searchAPI, notificationsAPI } from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import UserAvatar from '../UI/UserAvatar';
import {
  HomeIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  BellIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  SunIcon,
  MoonIcon,
  BuildingOfficeIcon,
  SwatchIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Discovery', href: '/discovery', icon: GlobeAltIcon },
  { name: 'Leads', href: '/leads', icon: UserGroupIcon },
  { name: 'Deals', href: '/deals', icon: BriefcaseIcon },
  { name: 'Quotes', href: '/quotes', icon: DocumentTextIcon },
  { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon },
  { name: 'Clients', href: '/clients', icon: UserGroupIcon },
  { name: 'Products', href: '/products', icon: ShoppingBagIcon },
  { name: 'Activity', href: '/activity', icon: ChartBarIcon },
];

const adminNavigation = [
  { name: 'Users', href: '/users', icon: UserGroupIcon },
];



// Tooltip rendered via portal to avoid sidebar overflow clipping
const SidebarTooltip = ({ text, anchorRect }) => {
  if (!anchorRect) return null;
  return ReactDOM.createPortal(
    <div
      className="fixed px-2.5 py-1 bg-gray-900 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap pointer-events-none"
      style={{
        left: anchorRect.right + 10,
        top: anchorRect.top + anchorRect.height / 2,
        transform: 'translateY(-50%)',
        zIndex: 99999,
      }}
    >
      {text}
    </div>,
    document.body
  );
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { darkMode, toggleDarkMode, primaryColor, changeThemeColor, colorPresets } = useTheme();
  const [hoveredTooltip, setHoveredTooltip] = useState(null); // { text, rect }
  const [hoveredNav, setHoveredNav] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Fetch unread notification count
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await notificationsAPI.getAll();
      return response.data.data;
    },
    refetchInterval: 30000,
  });
  const unreadCount = notifications?.filter((n) => !n.isRead).length || 0;

  // Compute current page info for breadcrumb
  const currentPage = useMemo(() => {
    const allNav = [...navigation, ...adminNavigation, { name: 'Settings', href: '/settings', icon: Cog6ToothIcon }, { name: 'Notifications', href: '/notifications', icon: BellIcon }];
    return allNav.find(item => location.pathname.startsWith(item.href));
  }, [location.pathname]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Ctrl+K / Cmd+K to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (val.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    setSearchOpen(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await searchAPI.global(val.trim());
        setSearchResults(res.data.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleSearchResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(result.href);
  };

  const ENTITY_ICONS = {
    lead: { color: 'bg-blue-500', label: 'Lead' },
    deal: { color: 'bg-emerald-500', label: 'Deal' },
    client: { color: 'bg-violet-500', label: 'Client' },
    organization: { color: 'bg-amber-500', label: 'Org' },
    product: { color: 'bg-pink-500', label: 'Product' },
    user: { color: 'bg-cyan-500', label: 'User' },
    quote: { color: 'bg-orange-500', label: 'Quote' },
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 transition-colors duration-300">
        {/* Skip to content link for accessibility */}
        <a href="#main-content" className="sr-only-focusable">
          Skip to main content
        </a>
        {/* Sidebar — always collapsed, icon-only */}
        <aside
          className="fixed inset-y-0 left-0 z-50 flex flex-col w-[60px] bg-surface-900 dark:bg-surface-900 border-r border-surface-800"
        >
          {/* Logo */}
          <div className="h-14 flex items-center justify-center border-b border-white/5">
            <Link to="/dashboard">
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center shadow-lg shadow-primary-600/30"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </motion.div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto overflow-x-hidden scrollbar-none">
            {navigation.map((item) => {
              const isActive = isActivePath(item.href);
              const isHovered = hoveredNav === item.name;
              return (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredTooltip({ text: item.name, rect });
                      setHoveredNav(item.name);
                    }}
                    onMouseLeave={() => { setHoveredTooltip(null); setHoveredNav(null); }}
                    className={`flex items-center justify-center px-2.5 py-2 rounded-lg transition-all duration-300 group relative ${
                      isActive
                        ? 'bg-primary-600/20 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-r-full"
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                    <motion.div
                      animate={isHovered ? { scale: 1.2, rotate: isActive ? 0 : -5 } : { scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    >
                      <item.icon className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${isActive ? 'text-primary-400' : ''}`} />
                    </motion.div>
                  </Link>
                </div>
              );
            })}

            {/* Admin Navigation */}
            {isAdmin() && (
              <>
                <div className="my-2 border-t border-white/5" />
                {adminNavigation.map((item) => {
                  const isActive = isActivePath(item.href);
                  const isHovered = hoveredNav === item.name;
                  return (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredTooltip({ text: item.name, rect });
                          setHoveredNav(item.name);
                        }}
                        onMouseLeave={() => { setHoveredTooltip(null); setHoveredNav(null); }}
                        className={`flex items-center justify-center px-2.5 py-2 rounded-lg transition-all duration-300 group relative ${
                          isActive
                            ? 'bg-primary-600/20 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeNav"
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-r-full"
                            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                          />
                        )}
                        <motion.div
                          animate={isHovered ? { scale: 1.2, rotate: -5 } : { scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                        >
                          <item.icon className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${isActive ? 'text-primary-400' : ''}`} />
                        </motion.div>
                      </Link>
                    </div>
                  );
                })}
              </>
            )}
          </nav>

          {/* Settings icon at bottom */}
          <div className="p-2 border-t border-white/5 flex justify-center">
            <Link
              to="/settings"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredTooltip({ text: 'Settings', rect });
              }}
              onMouseLeave={() => setHoveredTooltip(null)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </Link>
          </div>

          {/* Tooltip portal */}
          {hoveredTooltip && (
            <SidebarTooltip text={hoveredTooltip.text} anchorRect={hoveredTooltip.rect} />
          )}
        </aside>

        {/* Main Content */}
        <div className="pl-[60px]">
          {/* Top Bar */}
          <header
            className="sticky top-0 z-40 h-14 bg-white dark:bg-surface-800 border-b border-gray-200 dark:border-surface-700 shadow-sm"
          >
            <div className="h-full px-6 flex items-center justify-between">
              {/* Mega Search */}
              <div className="flex-1 max-w-lg" ref={searchRef}>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search leads, deals, clients, products..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                    className="w-full pl-9 pr-16 py-2 text-sm bg-gray-50 dark:bg-surface-900 border border-gray-200 dark:border-surface-600 rounded-lg focus:bg-white dark:focus:bg-surface-800 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100"
                  />
                  <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-surface-700 border border-gray-200 dark:border-surface-600 rounded">
                    Ctrl K
                  </kbd>
                  {/* Search Results Dropdown */}
                  <AnimatePresence>
                    {searchOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-gray-200 dark:border-surface-700 max-h-96 overflow-y-auto z-50"
                      >
                        {searchLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-2 text-sm text-gray-500">Searching...</span>
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            No results found for "{searchQuery}"
                          </div>
                        ) : (
                          <div className="py-1">
                            {searchResults.map((result, idx) => {
                              const entity = ENTITY_ICONS[result.type] || ENTITY_ICONS.lead;
                              return (
                                <button
                                  key={`${result.type}-${result.id}-${idx}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className="w-full flex items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-surface-700/50 transition-colors text-left"
                                >
                                  <span className={`w-2 h-2 rounded-full ${entity.color} flex-shrink-0`} />
                                  <div className="ml-3 flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {result.title}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {result.subtitle}
                                    </p>
                                  </div>
                                  <span className="ml-2 px-2 py-0.5 text-2xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-surface-700 rounded-md flex-shrink-0">
                                    {entity.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Breadcrumb / Page Title */}
              {currentPage && (
                <div className="hidden md:flex items-center mx-4 text-sm text-gray-500 dark:text-gray-400">
                  <HomeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <ChevronRightIcon className="w-3 h-3 mx-1.5 flex-shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-white">{currentPage.name}</span>
                </div>
              )}

              {/* Right Actions */}
              <div className="flex items-center space-x-1.5">
                {/* Color Theme */}
                <Menu as="div" className="relative">
                  <Menu.Button className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-700 transition-colors">
                    <SwatchIcon className="w-5 h-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-44 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-gray-200 dark:border-surface-700 p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
                        Theme Color
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {colorPresets.map((color) => (
                          <Menu.Item key={color}>
                            {({ active }) => (
                              <button
                                onClick={() => changeThemeColor(color)}
                                className={`h-8 rounded-lg transition-all duration-200 ${
                                  primaryColor === color ? 'ring-2 ring-offset-2 ring-primary-500 dark:ring-offset-surface-800' : 'hover:scale-110'
                                }`}
                                style={{
                                  background: `linear-gradient(135deg, ${
                                    color === 'blue' ? '#3b82f6' :
                                    color === 'purple' ? '#a855f7' :
                                    color === 'green' ? '#22c55e' :
                                    color === 'orange' ? '#f97316' :
                                    color === 'pink' ? '#ec4899' :
                                    '#14b8a6'
                                  }, ${
                                    color === 'blue' ? '#1d4ed8' :
                                    color === 'purple' ? '#7e22ce' :
                                    color === 'green' ? '#15803d' :
                                    color === 'orange' ? '#c2410c' :
                                    color === 'pink' ? '#be185d' :
                                    '#0f766e'
                                  })`
                                }}
                                title={color.charAt(0).toUpperCase() + color.slice(1)}
                              />
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                {/* Dark Mode */}
                <motion.button
                  onClick={toggleDarkMode}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9, rotate: 180 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-700 transition-colors"
                >
                  {darkMode ? <SunIcon className="w-5 h-5 text-amber-500" /> : <MoonIcon className="w-5 h-5" />}
                </motion.button>

                {/* Notifications */}
                <Link to="/notifications" className="relative p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-700 transition-colors group">
                  <motion.div whileHover={{ rotate: [0, -15, 15, -10, 10, 0] }} transition={{ duration: 0.5 }}>
                    <BellIcon className="w-5 h-5" />
                  </motion.div>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-sm shadow-red-500/30 animate-bounce-in">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Divider */}
                <div className="w-px h-6 bg-gray-300/50 dark:bg-gray-600/50 mx-1" />

                {/* User Menu */}
                <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center space-x-2.5 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-700 transition-colors">
                    <UserAvatar user={user} size="sm" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden md:block">
                      {user?.firstName}
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-52 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-gray-200 dark:border-surface-700 py-1">
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/settings"
                              className={`flex items-center space-x-2.5 px-4 py-2 text-sm transition-colors ${
                                active ? 'bg-gray-50 dark:bg-surface-700' : ''
                              } text-gray-700 dark:text-gray-200`}
                            >
                              <Cog6ToothIcon className="w-4 h-4 text-gray-400" />
                              <span>Settings</span>
                            </Link>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`w-full flex items-center space-x-2.5 px-4 py-2 text-sm transition-colors ${
                                active ? 'bg-red-50 dark:bg-red-900/10' : ''
                              } text-red-600 dark:text-red-400`}
                            >
                              <ArrowRightOnRectangleIcon className="w-4 h-4" />
                              <span>Sign out</span>
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main id="main-content" className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 12, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.99 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
